import {
  AlertTriangle,
  BrainCircuit,
  CheckCircle2,
  Clock,
  Inbox,
  Lightbulb,
  Loader2,
  Play,
  RefreshCw,
  Radar,
  RadioTower,
  SlidersHorizontal,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { SourceOperationsSheet } from "@/components/sources/source-operations-sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useInboxBatchExtraction } from "@/hooks/useInboxExtraction";
import { useOperatorStatus } from "@/hooks/useOperatorStatus";
import { useSources } from "@/hooks/useSources";
import {
  apiPost,
  type OperatorProcessorActionResponse,
  type OperatorProcessorStatus,
  type SignalAuditEvent,
  type SourceConfig,
} from "@/lib/api";

/** Processor cockpit: LLM extraction controls and conversion health. */
export default function OperatorProcessorPage() {
  const sourcesQ = useSources();
  const statusQ = useOperatorStatus();
  const analyze = useInboxBatchExtraction();
  const processorAction = useProcessorAction();
  const [sourceId, setSourceId] = useState("all");
  const [statusFilter, setStatusFilter] = useState("new");
  const [onlyUnstructured, setOnlyUnstructured] = useState(true);
  const [force, setForce] = useState(false);
  const [groupSize, setGroupSize] = useState(1);
  const [limit, setLimit] = useState(100);
  const sources = sourcesQ.data ?? [];
  const status = statusQ.data;
  const processor = status?.processor ?? null;
  const autoAnalyzeSources = sources.filter((source) => processingFromSource(source).auto_extract);
  const manualAnalyzeSources = sources.filter((source) => !processingFromSource(source).auto_extract);
  const processorEvents = processor?.recent_events ?? (status?.recent_audit_events ?? []).filter(isProcessorEvent);
  const lastProcessorEvent = processorEvents[0] ?? null;
  const recentFailures = processorEvents.filter(isProcessorFailure);
  const lastSummary = processorSummary(lastProcessorEvent);
  const sourceOptions = useMemo(
    () => [...sources].sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [sources],
  );

  const analyzeNewItems = () => {
    analyze.mutate({
      source_id: sourceId === "all" ? undefined : [sourceId],
      status: statusFilter === "any" ? undefined : statusFilter,
      only_unstructured: onlyUnstructured,
      force,
      group_size: groupSize,
      limit,
    });
  };

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-border px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold">Signal Processor</h1>
            <p className="text-xs text-muted-fg">
              Structured conversion, LLM extraction, and candidate readiness.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/operator/scraper"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium hover:bg-muted"
            >
              <Radar className="size-3.5" />
              Scraper
            </Link>
            <Link
              to="/operator/publish"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs font-medium hover:bg-muted"
            >
              <RadioTower className="size-3.5" />
              Publish
            </Link>
            <Button
              type="button"
              size="sm"
              onClick={analyzeNewItems}
              disabled={analyze.isPending}
              className="h-8"
            >
              <Play className="size-3.5" />
              Analyze new
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-4 sm:p-6">
        {sourcesQ.isLoading || statusQ.isLoading ? (
          <div className="py-10 text-center text-sm text-muted-fg">
            Loading processor controls...
          </div>
        ) : sourcesQ.isError || statusQ.isError ? (
          <div className="py-10 text-center text-sm text-red-300">
            Failed to load processor controls.
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <ProcessorMetric
                icon={BrainCircuit}
                label="Processor"
                value={processorStatusLabel(processor)}
                detail={processorDetail(processor)}
                tone={processorTone(processor)}
              />
              <ProcessorMetric
                icon={Inbox}
                label="Needs conversion"
                value={processor?.pending_new_unstructured ?? 0}
                detail={`${processor?.active_unstructured ?? 0} active unstructured`}
                tone={(processor?.pending_new_unstructured ?? 0) > 0 ? "neutral" : "ok"}
              />
              <ProcessorMetric
                icon={SlidersHorizontal}
                label="Auto analyze"
                value={autoAnalyzeSources.length}
                detail={`${manualAnalyzeSources.length} manual`}
                tone={autoAnalyzeSources.length > 0 ? "ok" : "neutral"}
              />
              <ProcessorMetric
                icon={CheckCircle2}
                label="Last conversion"
                value={lastSummary.converted}
                detail={lastProcessorEvent ? `${lastSummary.skipped} skipped` : "no activity"}
                tone={lastProcessorEvent ? "ok" : "neutral"}
              />
              <ProcessorMetric
                icon={AlertTriangle}
                label="Recent failures"
                value={recentFailures.length}
                detail={recentFailures[0]?.error_message ?? "none"}
                tone={recentFailures.length > 0 ? "error" : "ok"}
              />
              <ProcessorMetric
                icon={Clock}
                label="Last processor run"
                value={lastProcessorEvent ? formatShortDate(lastProcessorEvent.created_at) : "never"}
                detail={lastProcessorEvent?.event_type ?? "waiting"}
                tone="neutral"
              />
            </div>

            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
              <SourceProcessingPanel sources={sources} />
              <RunPanel
                pending={analyze.isPending}
                actionPending={processorAction.isPending}
                processor={processor}
                lastEvent={lastProcessorEvent}
                lastSummary={lastSummary}
                sources={sourceOptions}
                sourceId={sourceId}
                setSourceId={setSourceId}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                onlyUnstructured={onlyUnstructured}
                setOnlyUnstructured={setOnlyUnstructured}
                force={force}
                setForce={setForce}
                groupSize={groupSize}
                setGroupSize={setGroupSize}
                limit={limit}
                setLimit={setLimit}
                onAnalyzeNew={analyzeNewItems}
                onRunLoop={() => processorAction.mutate("run")}
                onPauseLoop={() => processorAction.mutate("pause")}
                onResumeLoop={() => processorAction.mutate("resume")}
              />
            </div>

            <ProcessorActivityFeed events={processorEvents} />
          </div>
        )}
      </div>
    </div>
  );
}

function SourceProcessingPanel({ sources }: { sources: SourceConfig[] }) {
  const sorted = [...sources].sort((a, b) => {
    const aAuto = processingFromSource(a).auto_extract;
    const bAuto = processingFromSource(b).auto_extract;
    if (aAuto !== bAuto) return aAuto ? -1 : 1;
    return a.display_name.localeCompare(b.display_name);
  });

  return (
    <section className="overflow-hidden rounded-md border border-border bg-bg/40">
      <div className="border-b border-border/70 px-3 py-2.5">
        <h2 className="text-sm font-semibold">Source processing modes</h2>
        <p className="text-xs text-muted-fg">
          Controls whether captured inbox rows are converted automatically after scraper data arrives.
        </p>
      </div>
      {sorted.length === 0 ? (
        <div className="px-3 py-8 text-center text-sm text-muted-fg">
          No scraper configs found.
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {sorted.map((source) => (
            <SourceProcessingRow key={source.id} source={source} />
          ))}
        </div>
      )}
    </section>
  );
}

function SourceProcessingRow({ source }: { source: SourceConfig }) {
  const processing = processingFromSource(source);
  const vendor = source.vendor ?? "unknown";
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 text-sm">
      <div className="min-w-0">
        <Link
          to={`/operator/scraper?vendor=${encodeURIComponent(vendor)}`}
          className="truncate font-medium hover:text-accent hover:underline"
        >
          {source.display_name}
        </Link>
        <div className="truncate font-mono text-[11px] text-muted-fg">
          {vendor} / {source.id}
        </div>
      </div>
      <div className="min-w-0 flex-1" />
      <Badge variant={processing.auto_extract ? "success" : "outline"}>
        {processing.auto_extract ? "auto" : "manual"}
      </Badge>
      <span className="text-xs text-muted-fg">group {processing.group_size}</span>
      <SourceOperationsSheet
        source={source}
        triggerClassName="h-7 gap-1 px-2 text-xs"
      />
    </div>
  );
}

function RunPanel({
  pending,
  actionPending,
  processor,
  lastEvent,
  lastSummary,
  sources,
  sourceId,
  setSourceId,
  statusFilter,
  setStatusFilter,
  onlyUnstructured,
  setOnlyUnstructured,
  force,
  setForce,
  groupSize,
  setGroupSize,
  limit,
  setLimit,
  onAnalyzeNew,
  onRunLoop,
  onPauseLoop,
  onResumeLoop,
}: {
  pending: boolean;
  actionPending: boolean;
  processor: OperatorProcessorStatus | null;
  lastEvent: SignalAuditEvent | null;
  lastSummary: ProcessorSummary;
  sources: SourceConfig[];
  sourceId: string;
  setSourceId: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  onlyUnstructured: boolean;
  setOnlyUnstructured: (value: boolean) => void;
  force: boolean;
  setForce: (value: boolean) => void;
  groupSize: number;
  setGroupSize: (value: number) => void;
  limit: number;
  setLimit: (value: number) => void;
  onAnalyzeNew: () => void;
  onRunLoop: () => void;
  onPauseLoop: () => void;
  onResumeLoop: () => void;
}) {
  const disabled = pending || processor?.running === true || processor?.reachable === false;
  const actionDisabled = actionPending || processor?.reachable === false;
  return (
    <section className="rounded-md border border-border bg-bg/40 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Run conversion</h2>
          <p className="mt-1 text-xs text-muted-fg">
            Converts new inbox rows that do not yet have structured payloads.
          </p>
        </div>
        <Button type="button" size="sm" onClick={onAnalyzeNew} disabled={disabled}>
          {pending || processor?.running ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />}
          Analyze
        </Button>
      </div>

      <div className="mt-4 grid gap-3 text-sm">
        <label className="grid gap-1.5">
          <span className="text-xs font-medium text-muted-fg">Source</span>
          <Select value={sourceId} onValueChange={setSourceId}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sources.map((source) => (
                <SelectItem key={source.id} value={source.id}>
                  {source.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="grid gap-1.5">
            <span className="text-xs font-medium text-muted-fg">Inbox status</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="ack">Read</SelectItem>
                <SelectItem value="any">Any</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <NumberField label="Group" value={groupSize} min={1} max={20} onChange={setGroupSize} />
          <NumberField label="Limit" value={limit} min={1} max={200} onChange={setLimit} />
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-muted-fg">
          <label className="inline-flex items-center gap-2">
            <input
              className="size-4 accent-accent"
              type="checkbox"
              checked={onlyUnstructured}
              onChange={(event) => {
                const enabled = event.target.checked;
                setOnlyUnstructured(enabled);
                if (enabled) setForce(false);
              }}
            />
            Only unstructured
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              className="size-4 accent-accent"
              type="checkbox"
              checked={force}
              onChange={(event) => {
                const enabled = event.target.checked;
                setForce(enabled);
                if (enabled) setOnlyUnstructured(false);
              }}
            />
            Force re-analyze
          </label>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
        <MiniCount label="new backlog" value={processor?.pending_new_unstructured ?? 0} />
        <MiniCount label="active backlog" value={processor?.active_unstructured ?? 0} />
        <MiniCount label="failed last" value={lastSummary.failed} />
      </div>

      <div className="mt-4 grid gap-2 rounded-md bg-muted/30 p-3 text-xs sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
        <div className="min-w-0">
          <div className="font-medium text-fg">
            Auto loop {processor?.loop_enabled ? (processor.loop_paused ? "paused" : "running") : "disabled"}
          </div>
          <div className="truncate text-muted-fg">
            {processor?.loop_next_tick_at
              ? `next ${formatFullDate(processor.loop_next_tick_at)}`
              : processor?.loop_last_tick_at
                ? `last ${formatFullDate(processor.loop_last_tick_at)}`
                : "waiting for first tick"}
            {processor?.loop_interval_seconds ? ` · every ${processor.loop_interval_seconds}s` : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={onRunLoop} disabled={actionDisabled || processor?.running === true}>
            {actionPending ? <Loader2 className="size-3.5 animate-spin" /> : <Play className="size-3.5" />}
            Run now
          </Button>
          {processor?.loop_paused ? (
            <Button type="button" size="sm" variant="outline" onClick={onResumeLoop} disabled={actionDisabled}>
              Resume loop
            </Button>
          ) : (
            <Button type="button" size="sm" variant="outline" onClick={onPauseLoop} disabled={actionDisabled}>
              Pause loop
            </Button>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        <Link
          to="/inbox"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 hover:bg-muted"
        >
          <Inbox className="size-3.5" />
          Inbox
        </Link>
        <Link
          to="/candidates"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 hover:bg-muted"
        >
          <Lightbulb className="size-3.5" />
          Candidates
        </Link>
      </div>

      <div className="mt-4 rounded-md bg-muted/35 px-3 py-2 text-xs text-muted-fg">
        {processor?.running ? (
          <>
            Processor is running <span className="text-fg">{processor.active_run_kind ?? "a batch"}</span>
            {processor.active_started_at ? ` since ${formatFullDate(processor.active_started_at)}` : ""}.
          </>
        ) : processor?.error ? (
          <span className="text-red-300">{processor.error}</span>
        ) : lastEvent ? (
          <>
            Last processor activity was <span className="text-fg">{lastEvent.status}</span> at{" "}
            {formatFullDate(lastEvent.created_at)}.
          </>
        ) : (
          "No processor audit event has been recorded yet."
        )}
      </div>
    </section>
  );
}

function ProcessorActivityFeed({ events }: { events: SignalAuditEvent[] }) {
  return (
    <section className="overflow-hidden rounded-md border border-border bg-bg/40">
      <div className="flex items-center gap-2 border-b border-border/70 px-3 py-2.5 text-sm font-semibold">
        <RefreshCw className="size-4" />
        Recent processor activity
      </div>
      {events.length === 0 ? (
        <div className="px-3 py-8 text-center text-sm text-muted-fg">
          No processor activity yet.
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {events.slice(0, 12).map((event) => (
            <ProcessorActivityRow key={event.id} event={event} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProcessorActivityRow({ event }: { event: SignalAuditEvent }) {
  const summary = processorSummary(event);
  return (
    <div className="grid gap-2 px-3 py-2.5 text-xs md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center">
      <div className="flex items-center gap-2">
        <Badge variant={isProcessorFailure(event) ? "danger" : "success"}>{event.status}</Badge>
        <span className="text-muted-fg">{event.trigger_kind ?? "system"}</span>
      </div>
      <div className="min-w-0">
        <div className="truncate font-medium">{event.event_type}</div>
        <div className="truncate text-muted-fg">
          converted {summary.converted} · skipped {summary.skipped} · failed {summary.failed}
          {event.error_message ? ` · ${event.error_message}` : ""}
        </div>
      </div>
      <div className="text-muted-fg md:text-right">{formatFullDate(event.created_at)}</div>
    </div>
  );
}

function ProcessorMetric({
  icon: Icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  detail: string;
  tone: "ok" | "error" | "neutral";
}) {
  return (
    <div className="rounded-md border border-border bg-bg/40 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-fg">
        <Icon className={metricIconClass(tone)} />
        {label}
      </div>
      <div className="mt-2 truncate text-2xl font-semibold tabular-nums">{value}</div>
      <div className="mt-1 truncate text-xs text-muted-fg">{detail}</div>
    </div>
  );
}

function MiniCount({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-muted/35 px-2 py-2">
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] text-muted-fg">{label}</div>
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="grid gap-1.5">
      <span className="text-xs font-medium text-muted-fg">{label}</span>
      <Input
        className="h-9"
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(boundedNumber(Number(event.target.value), value, min, max))}
      />
    </label>
  );
}

interface ProcessorSummary {
  converted: number;
  skipped: number;
  failed: number;
}

function processorSummary(event: SignalAuditEvent | null): ProcessorSummary {
  const metadata = event?.metadata;
  return {
    converted: metadataNumber(metadata, "processing_converted_count"),
    skipped: metadataNumber(metadata, "processing_skipped_count"),
    failed: metadataNumber(metadata, "processing_failed_count"),
  };
}

function isProcessorEvent(event: SignalAuditEvent): boolean {
  return (
    event.event_type === "signal_processing_completed" ||
    event.error_type === "signal_data_processor" ||
    metadataString(event.metadata, "processing_status") === "failed"
  );
}

function isProcessorFailure(event: SignalAuditEvent): boolean {
  const summary = processorSummary(event);
  return (
    event.error_type === "signal_data_processor" ||
    metadataString(event.metadata, "processing_status") === "failed" ||
    summary.failed > 0
  );
}

function processingFromSource(source: SourceConfig): {
  auto_extract: boolean;
  group_size: number;
} {
  const processing = source.config?.processing;
  if (!isRecord(processing)) return { auto_extract: false, group_size: 1 };
  return {
    auto_extract: processing.auto_extract === true,
    group_size: boundedNumber(processing.group_size, 1, 1, 20),
  };
}

function metadataNumber(metadata: Record<string, unknown> | null | undefined, key: string): number {
  const value = metadata?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function metadataString(metadata: Record<string, unknown> | null | undefined, key: string): string {
  const value = metadata?.[key];
  return typeof value === "string" ? value : "";
}

function metricIconClass(tone: "ok" | "error" | "neutral"): string {
  const base = "size-4";
  switch (tone) {
    case "ok":
      return `${base} text-emerald-300`;
    case "error":
      return `${base} text-red-300`;
    case "neutral":
      return `${base} text-muted-fg`;
  }
}

function processorStatusLabel(processor: OperatorProcessorStatus | null): string {
  if (!processor) return "loading";
  if (!processor.reachable) return "offline";
  if (processor.running) return "running";
  return "ready";
}

function processorDetail(processor: OperatorProcessorStatus | null): string {
  if (!processor) return "waiting";
  if (processor.error) return processor.error;
  if (processor.last_error) return processor.last_error;
  if (processor.loop_enabled) return processor.loop_paused ? "loop paused" : "auto loop on";
  if (processor.last_run_at) return `last ${formatFullDate(processor.last_run_at)}`;
  return processor.service_url || processor.transport;
}

function processorTone(processor: OperatorProcessorStatus | null): "ok" | "error" | "neutral" {
  if (!processor) return "neutral";
  if (!processor.reachable || processor.error || processor.last_error) return "error";
  if (processor.running || processor.completed_runs > 0) return "ok";
  return "neutral";
}

function boundedNumber(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(Math.max(value, min), max);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function formatShortDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatFullDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function useProcessorAction() {
  const qc = useQueryClient();
  return useMutation<OperatorProcessorActionResponse, Error, "run" | "pause" | "resume">({
    mutationFn: (action) => apiPost<OperatorProcessorActionResponse>(`/api/v1/operator/processor/${action}`),
    onSuccess: (result, action) => {
      const summary = result.summary;
      const converted = typeof summary?.converted_count === "number" ? summary.converted_count : null;
      toast.success(
        action === "run" && converted !== null
          ? `Processor run completed: ${converted} converted`
          : `Processor ${result.status}`,
      );
      qc.invalidateQueries({ queryKey: ["operator-status"] });
      qc.invalidateQueries({ queryKey: ["inbox"] });
      qc.invalidateQueries({ queryKey: ["signal-candidates"] });
    },
    onError: (err) => {
      toast.error(`Processor action failed: ${err.message}`);
    },
  });
}
